import React, { useState, useRef, useEffect } from "react";
import { useAppLibs } from "../../ContextProvider/AppLib_Provider";
import Utils from "../../utils/utils";
import { EditButton, CancelButton, SaveButton } from "../../utils/Buttons";
import RadarChart from "./RadarChart ";
import LineChart from "./LineChart";
import Card from "./Card";
import CircularProgress from "./CircularProgress";

const StudentProgressRecord = ({ student }) => {
  const { Students } = useAppLibs();
  const STUDENT_CRITERIA = Students.STUDENT_CRITERIA;
  const assessmentCriteria = Object.keys(STUDENT_CRITERIA);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAssessment, setEditedAssessment] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [progressData, setProgressData] = useState([]);
  const [assessments, setAssessments] = useState(progressData);
  const currentRecord = assessments[selectedMonth];
  const [overallScore, setOverallScore] = useState(null);
  const scoreRef = useRef(null);

  const fetchStudentProgress = async (student) => {
    if (!student._id) return;
    const result = await Students.getStudentProgressData({
      id: student._id,
      createdAt: student.createdAt,
    });
    if (result.success) {
      const data = result.data;
      setProgressData(data);
      setAssessments(data);
    } else {
      console.error("Error:", result.error);
    }
  };

  useEffect(() => {
    fetchStudentProgress(student);
  }, [student]);

  const validateAssessment = (value) => {
    if (value === "" || value === null || value === undefined)
      return "Value is required";

    const stringValue = String(value); // Convert number to string if needed

    // Preserve '0.x' format but remove unnecessary leading zeros
    const cleanValue = stringValue.startsWith("0.")
      ? stringValue
      : stringValue.replace(/^0+/, "");

    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return "Must be a number";
    if (numValue < 0 || numValue > 10) return "Must be between 0 and 10";

    // Check if the value has more than 1 decimal place
    if (cleanValue.includes(".") && cleanValue.split(".")[1].length > 1) {
      return "Maximum 1 decimal place allowed";
    }

    return null;
  };

  const handleMonthSelect = (index) => {
    if (overallScore) setOverallScore(null);
    if (assessments[index].enabled) {
      setSelectedMonth(index);
      setIsEditing(false);
      setEditedAssessment({});
      setValidationErrors({});
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedAssessment({ ...currentRecord.assessment });
    setValidationErrors({});
  };

  const handleSave = async () => {
    // Validate all fields
    const errors = {};
    let hasErrors = false;
    const normalizedAssessment = {};

    assessmentCriteria.forEach((criterion) => {
      const value = editedAssessment[criterion] || "";
      const error = validateAssessment(value);

      if (error) {
        errors[criterion] = error;
        hasErrors = true;
      } else {
        const stringValue = String(value); // Convert number to string if needed
        // Preserve '0.x' format but remove unnecessary leading zeros
        const cleanValue = stringValue.startsWith("0.")
          ? stringValue
          : stringValue.replace(/^0+/, "");

        const numValue = parseFloat(cleanValue);
        // Store normalized value (remove leading zeros)
        // const cleanValue = value.replace(/^0+/, "") || "0";
        normalizedAssessment[criterion] = numValue;
      }
    });

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    const formData = {
      studentId: student._id,
      month: currentRecord.month,
      assessment: normalizedAssessment,
      createdAt: Utils.getCurrentTime(),
    };
    const result = await Students.addStudentProgress(formData);
    if (result.success) {
      const data = result.data;
      //   setProgressData(data);
      //   setAssessments(data);
      setAssessments((prevAssessments) => {
        const newAssessments = [...prevAssessments];
        newAssessments[selectedMonth] = {
          ...newAssessments[selectedMonth],
          assessment: data.assessment,
        };
        return newAssessments;
      });

      setIsEditing(false);
      setValidationErrors({});
    } else {
      console.error("Error:", result.error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedAssessment({});
    setValidationErrors({});
  };

  const handleAssessmentChange = (criterion, value) => {
    const sanitizedValue = value.replace(/[^\d.]/g, "");
    setEditedAssessment((prev) => ({
      ...prev,
      [criterion]: sanitizedValue,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[criterion]) {
      setValidationErrors((prev) => ({
        ...prev,
        [criterion]: null,
      }));
    }
  };

  const handleScroll = () => {
    scoreRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const calculateOverallScores = () => {
    const ret = progressData.reduce((acc, { month, assessment }) => {
      const values = Object.values(assessment);
      const m = Utils.formatDateTime(month);
      acc[m] =
        values.length > 0
          ? values.reduce((sum, val) => sum + val, 0) / values.length
          : 0;
      return acc;
    }, {});
    setOverallScore(ret);
    handleScroll();
  };

  const enabledAssessments = progressData.filter((d) => d.enabled);
  const completedAssessments = enabledAssessments.filter(
    (d) => Object.keys(d.assessment).length > 0
  );

  const pendingAssessments =
    enabledAssessments.length - completedAssessments.length;

  const allScores = completedAssessments.flatMap((d) =>
    Object.values(d.assessment)
  );

  const profileScore =
    allScores.length > 0
      ? (
          (allScores.reduce((a, b) => a + b, 0) / (allScores.length * 10)) *
          100
        ).toFixed(1) + "%"
      : "N/A";

  const lastAssessment = completedAssessments.at(-1);
  const lastScore = lastAssessment
    ? (
        (Object.values(lastAssessment.assessment).reduce((a, b) => a + b, 0) /
          (Object.values(lastAssessment.assessment).length * 10)) *
        100
      ).toFixed(1) + "%"
    : "N/A";
  return (
    <div className="w-full mx-auto bg-white">
      {/* Progress Timeline */}
      <div className="mb-4">
        <h2 className="text-md font-semibold mb-3">Progress Timeline</h2>
        <div className="flex space-x-2 overflow-x-auto pb-4">
          {assessments.map((item, index) => (
            <button
              key={index}
              onClick={() => handleMonthSelect(index)}
              title={
                selectedMonth !== index && item.enabled
                  ? "Click to track progress"
                  : "Not enabled yet"
              }
              disabled={!item.enabled}
              className={`text-sm px-4 py-1.5 text-nowrap rounded-lg transition-colors ${
                selectedMonth === index && item.enabled
                  ? "bg-blue-500 text-white"
                  : item.enabled
                  ? "bg-blue-100 hover:bg-blue-200"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {Utils.formatDateTime(item.month)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-100 rounded-lg shadow-md">
          <Card title="Last Score" value={lastScore} color="bg-blue-500" />
          <Card
            title="Profile Score"
            value={profileScore}
            color="bg-green-500"
          />
          <CircularProgress pending={pendingAssessments} total={8} />
          <Card
            title="Total Assessments"
            value={completedAssessments.length}
            color="bg-purple-500"
          />
        </div>
      </div>

      {/* Current Month Assessment */}
      {currentRecord && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4">
          <div className="rounded-lg shadow-md">
            <div className="flex justify-between border-b p-4">
              <h3 className="text-lg font-semibold">
                Assessment for {Utils.formatDateTime(currentRecord.month)}
              </h3>
              <div className="">
                {isEditing ? (
                  <div className="flex space-x-2">
                    <SaveButton onClick={handleSave} />
                    <CancelButton onClick={handleCancel} />
                  </div>
                ) : (
                  <EditButton onClick={handleEditClick} />
                )}
              </div>
            </div>

            <div className="divide-y text-sm">
              {assessmentCriteria.map((criterion, index) => (
                <div
                  key={index}
                  className="py-3 px-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <h4 className="text-gray-700">
                      {STUDENT_CRITERIA[criterion]}
                    </h4>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          value={editedAssessment[criterion] || ""}
                          onChange={(e) =>
                            handleAssessmentChange(criterion, e.target.value)
                          }
                          className={`border rounded px-2 py-1 w-20 bg-gray-50  focus:border-pink-800 focus:bg-white focus:outline-none ${
                            validationErrors[criterion] ? "border-red-500" : ""
                          }`}
                          placeholder="Enter score (0-10)"
                        />
                        {validationErrors[criterion] && (
                          <span className="text-red-500 text-sm">
                            {validationErrors[criterion]}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-600">
                        {currentRecord.assessment[criterion] || "Not assessed"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            {Object.values(currentRecord.assessment).length > 0 ? (
              <div className="flex flex-col h-full justify-between rounded-md  bg-blue-50">
                {overallScore ? (
                  <RadarChart
                    assessment={overallScore}
                    labels={Object.keys(overallScore)}
                  />
                ) : (
                  <RadarChart assessment={currentRecord.assessment} />
                )}

                <button
                  onClick={calculateOverallScores}
                  className="w-fit self-center px-6 py-2 bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded-lg shadow-md transition-all duration-300 ease-in-out"
                >
                  Show Overall Score
                </button>
              </div>
            ) : (
              <div className="">
                <p className="text-md text-red-500">
                  Please complete the assesment to see the statics
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div ref={scoreRef}>
        {overallScore && (
          <LineChart
            assessment={overallScore}
            labels={Object.keys(overallScore)}
          />
        )}
      </div>
    </div>
  );
};

export default StudentProgressRecord;
